<template>
  <div>
    <transition name="modal-fade">
      <div
        v-if="showModal"
        class="draggable-overlay"
        :class="{ 'dragging-active': !isInitialPosition }"
      >
        <div
          ref="modalRef"
          class="draggable-panel"
          :class="{ 'is-dragging': isDragging }"
          :style="modalStyle"
          @click.stop
          @mousedown="startDrag"
          @touchstart="startDrag"
        >
          <div class="panel-header draggable">
            <div class="panel-header__text">
              <h2 class="panel-header__title">Note History</h2>
              <p class="panel-header__subtitle">View and manage contact notes</p>
            </div>
            <div class="panel-header__actions">
              <div class="drag-handle" title="Drag to move">
                <Icon icon="mdi:drag" width="16" height="16" />
              </div>
              <button
                type="button"
                class="panel-header__close"
                @click="closeModal"
                aria-label="Close modal"
              >
                <Icon icon="mdi:close" />
              </button>
            </div>
          </div>

          <div class="panel-body">
            <div class="modal-body-content">
              <div v-if="notesIsLoading" class="loading-state">
                <LoadingBars size="sm" />
              </div>

              <!-- Empty State -->
              <div
                v-else-if="!notesIsLoading && (!notes || notes.length === 0) && !showAddNoteForm"
                class="empty-notes"
              >
                <div class="empty-notes__inner">
                  <div class="empty-icon-wrapper">
                    <Icon icon="mdi:note-plus-outline" width="28" height="28" class="empty-icon" />
                  </div>
                  <div class="empty-text">
                    <p class="empty-title">No notes yet</p>
                    <p class="empty-description">
                      Add notes to track conversations and details for this contact.
                    </p>
                  </div>
                  <Button variant="primary" icon="mdi:plus" @click="showAddNoteForm = true">
                    Add note
                  </Button>
                </div>
              </div>

              <!-- Notes List -->
              <template v-else-if="!notesIsLoading">
                <div v-if="showAddNoteForm" class="add-note-form">
                  <div class="form-group">
                    <label class="add-note-label">Note</label>
                    <RichTextEditor
                      ref="noteEditor"
                      v-model="registerNoteObj.message"
                      height="150px"
                      :toolbar="notesToolbar"
                      :accepts-uploads="true"
                      @upload="handleEditorUpload"
                      @uploads-pending="pendingUploads = $event"
                    />
                  </div>
                  <div class="add-note-actions">
                    <Button variant="secondary" @click="cancelAddNote"> Cancel </Button>
                    <Button
                      variant="primary"
                      @click="registerNote"
                      :loading="isLoading && !isDeletingNote"
                      :disabled="!registerNoteObj.message?.trim() || pendingUploads > 0"
                    >
                      Save
                    </Button>
                  </div>
                </div>

                <template v-if="notes && notes.length > 0">
                  <div v-for="(note, index) in notes" :key="index" class="note-card">
                    <div class="note-card-header">
                      <NotePreview class="note-message" :html="parseNote(note.message)" />
                      <div class="note-actions">
                        <span
                          v-if="attachmentCount(note.id) > 0"
                          class="btn-action btn-attachments"
                          :title="`${attachmentCount(note.id)} attachment${attachmentCount(note.id) === 1 ? '' : 's'}`"
                        >
                          <Icon icon="mdi:paperclip" />
                          <span class="btn-action__count">{{ attachmentCount(note.id) }}</span>
                        </span>
                        <button
                          @click="selectNoteToEdit(note)"
                          class="btn-action btn-edit"
                          :disabled="isLoading || isDeletingNote"
                          title="Edit note"
                        >
                          <Icon icon="mdi:pencil-outline" />
                        </button>
                        <button
                          @click="toggleDeleteConfirmationModal(note)"
                          class="btn-action btn-delete"
                          :disabled="isLoading"
                          title="Delete note"
                        >
                          <Icon icon="mdi:delete-outline" />
                        </button>
                      </div>
                    </div>
                    <div class="note-card-footer">
                      <div class="meta-item">
                        <Icon icon="octicon:person-16" class="meta-icon" />
                        <span class="meta-text">{{ note.createdBy.fullName }}</span>
                      </div>
                      <div class="meta-date-time">
                        <span class="meta-date-text">{{ getDateOnly(note.createdAt) }}</span>
                        <span class="meta-time-text">{{ getTimeOnly(note.createdAt) }}</span>
                      </div>
                    </div>
                  </div>
                </template>
              </template>
            </div>
          </div>

          <div v-if="!showAddNoteForm && notes && notes.length > 0" class="panel-footer">
            <Button variant="primary" block icon="mdi:plus" @click="showAddNoteForm = true">
              Add Note
            </Button>
          </div>
        </div>
      </div>
    </transition>

    <!-- Child modals — outside draggable panel for proper viewport centering -->
    <EditNoteModal
      v-if="showEditNoteModal"
      :selectedNote="selectedNote"
      @close-edit-note-modal="showEditNoteModal = false"
      @refresh-notes="refetchNotes()"
    />
    <DeleteConfirmationModal
      v-if="showDeleteConfirmationModal"
      :deleteFunction="deleteNote"
      entity="Note"
      :plural="false"
      @close-delete-confirmation-modal="showDeleteConfirmationModal = false"
      @refresh-records="refetchNotes()"
    />
    <ActionConfirmationModal
      :show="showCloseConfirm"
      title="Discard upload?"
      message="Uploads are still in progress. Closing now will cancel them and discard the draft attachments."
      confirm-text="Discard"
      cancel-text="Keep open"
      @close="showCloseConfirm = false"
      @confirm="confirmCloseAndDiscard"
    />
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, onBeforeUnmount, watch } from "vue";
import { useStore } from "vuex";
import { v4 as uuidv4 } from "uuid";
import { Button, RichTextEditor, Icon } from "@/components/ui";
import LoadingBars from "@/components/ui/LoadingBars.vue";
import SafeHtml from "@/components/ui/SafeHtml.vue";
import NotePreview from "@/components/ui/NotePreview.vue";
import DeleteConfirmationModal from "@/components/modal/DeleteConfirmationModal.vue";
import ActionConfirmationModal from "@/components/modal/ActionConfirmationModal.vue";
import EditNoteModal from "@/components/note/EditNoteModal.vue";
import filters from "@/utils/filters";
import { useToast } from "@/composables/ui/useToast";
import { useFormDraft } from "@/composables/shared";
import {
  useNotes,
  useRegisterNote,
  useDeleteNote,
  useUploadDraftNoteAttachment,
  useDeleteNoteDraftAttachments,
} from "@/composables/notes";

// Props
const props = withDefaults(
  defineProps<{
    selectedContact: {
      id: string;
      [key: string]: any;
    };
    startInAddMode?: boolean;
  }>(),
  {
    startInAddMode: false,
  }
);

// Emits
const emit = defineEmits<{
  closeNotesModal: [];
}>();

// Store (only for account)
const store = useStore();
const account = computed(() => store.getters.account.data);
const organizationId = computed(() => account.value?.organizationId);

// Notes query
const notesParams = computed(() => {
  if (!props.selectedContact?.id || !organizationId.value) {
    return null;
  }
  return {
    contactId: props.selectedContact.id,
    organizationId: organizationId.value,
  };
});

const { data: notesData, isLoading: notesIsLoading, refetch: refetchNotes } = useNotes(notesParams);

// Register note mutation
const registerNoteMutation = useRegisterNote();
const isLoading = computed(() => registerNoteMutation.isPending.value);

// Delete note mutation
const { deleteNote: deleteNoteMutation, isDeleting: isDeletingNote } = useDeleteNote();

// Computed
const notes = computed(() => notesData.value || []);

// State
const showModal = ref(true);
const showEditNoteModal = ref(false);
const showDeleteConfirmationModal = ref(false);
const selectedNote = ref<any>(null);
const showAddNoteForm = ref(false);
const registerNoteObj = ref({
  message: null as string | null,
  contactId: null as string | null,
  organizationId: null as string | null,
});

const notesToolbar = [
  [{ header: [1, 2, 3, false] }],
  ["bold", "italic", "underline", "strike"],
  [{ color: [] }],
  [{ list: "ordered" }, { list: "bullet" }],
  ["link", "image", "attach-file"],
  ["clean"],
];

const draftKey = ref<string | null>(null);
const pendingUploads = ref(0);
const showCloseConfirm = ref(false);
const toast = useToast();
let beforeUnloadHandler: ((e: BeforeUnloadEvent) => void) | null = null;

const uploadDraft = useUploadDraftNoteAttachment();
const discardDraft = useDeleteNoteDraftAttachments();

// Shared with NextTaskFollowupModal.vue's "Add Note" field — both are just
// different entry points for jotting an unsaved note about the same contact,
// so they read/write the same draft under a common contact-scoped key.
const { loadDraft: loadNoteTextDraft, clearDraft: clearNoteTextDraft } = useFormDraft(
  registerNoteObj,
  {
    key: `contact-note-draft:${props.selectedContact?.id}`,
    serialize: (v) => ({ text: v.message ?? "" }),
    isEmpty: (v) => !v.text?.trim(),
  }
);

const handleEditorUpload = async (payload: {
  file: File;
  kind: "image" | "video" | "file";
  placeholderId?: string;
  signal?: AbortSignal;
  onProgress?: (pct: number) => void;
  onSuccess?: (url: string) => void;
  onError?: (msg?: string) => void;
}) => {
  try {
    if (!draftKey.value) draftKey.value = uuidv4();
    const result = await uploadDraft.mutateAsync({
      draftKey: draftKey.value,
      files: [payload.file],
      onProgress: payload.onProgress,
      signal: payload.signal,
    });
    const uploaded = result?.[0];
    if (uploaded?.url && payload.onSuccess) {
      payload.onSuccess(uploaded.url);
    } else {
      payload.onError?.("Upload failed");
    }
  } catch (err: any) {
    if (err?.code === "ERR_CANCELED" || err?.name === "AbortError") return;
    payload.onError?.(err?.message || "Upload failed");
  }
};

watch(pendingUploads, (n) => {
  if (n > 0 && !beforeUnloadHandler) {
    beforeUnloadHandler = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      e.returnValue = "Uploads in progress. Leave anyway?";
    };
    window.addEventListener("beforeunload", beforeUnloadHandler);
  } else if (n === 0 && beforeUnloadHandler) {
    window.removeEventListener("beforeunload", beforeUnloadHandler);
    beforeUnloadHandler = null;
  }
});

watch(showAddNoteForm, (open) => {
  if (open) {
    if (!draftKey.value) draftKey.value = uuidv4();
    if (!registerNoteObj.value.message?.trim()) {
      const draft = loadNoteTextDraft();
      if (draft?.text) registerNoteObj.value.message = draft.text;
    }
  }
});

// Template ref
const noteEditor = ref<any>(null);

// Drag state
const isDragging = ref(false);
const dragStart = ref({ x: 0, y: 0 });
const modalPosition = ref({ x: 0, y: 0 });
const isInitialPosition = ref(true);
const modalRef = ref<HTMLElement | null>(null);

const modalStyle = computed(() => {
  if (isInitialPosition.value) return {};
  return {
    position: "fixed" as const,
    left: `${modalPosition.value.x}px`,
    top: `${modalPosition.value.y}px`,
    transform: "none",
    margin: "0",
  };
});

// Drag handlers
const startDrag = (event: MouseEvent | TouchEvent) => {
  const target = event.target as HTMLElement;
  const excludedSelectors = [
    ".panel-body",
    ".panel-footer",
    "button",
    "a",
    "input",
    "textarea",
    ".ql-toolbar",
    ".ql-editor",
  ];

  if (excludedSelectors.some((selector) => target.closest(selector))) {
    return;
  }

  event.preventDefault();
  event.stopPropagation();
  isDragging.value = true;

  const clientX = "touches" in event ? event.touches[0].clientX : event.clientX;
  const clientY = "touches" in event ? event.touches[0].clientY : event.clientY;

  if (isInitialPosition.value) {
    const modal = modalRef.value;
    if (modal) {
      const rect = modal.getBoundingClientRect();
      modalPosition.value = { x: rect.left, y: rect.top };
    } else {
      modalPosition.value = {
        x: (window.innerWidth - 480) / 2,
        y: (window.innerHeight - 400) / 2,
      };
    }
    isInitialPosition.value = false;
  }

  dragStart.value = {
    x: clientX - modalPosition.value.x,
    y: clientY - modalPosition.value.y,
  };

  document.addEventListener("mousemove", handleDrag, { passive: false });
  document.addEventListener("mouseup", stopDrag);
  document.addEventListener("touchmove", handleDrag, { passive: false });
  document.addEventListener("touchend", stopDrag);
};

const handleDrag = (event: MouseEvent | TouchEvent) => {
  if (!isDragging.value) return;

  event.preventDefault();
  event.stopPropagation();

  const clientX = "touches" in event ? event.touches[0].clientX : event.clientX;
  const clientY = "touches" in event ? event.touches[0].clientY : event.clientY;

  let newX = clientX - dragStart.value.x;
  let newY = clientY - dragStart.value.y;

  const modal = modalRef.value;
  if (modal) {
    const rect = modal.getBoundingClientRect();
    const padding = 10;
    newX = Math.max(padding, Math.min(newX, window.innerWidth - rect.width - padding));
    newY = Math.max(padding, Math.min(newY, window.innerHeight - rect.height - padding));
  }

  modalPosition.value = { x: newX, y: newY };
};

const stopDrag = () => {
  isDragging.value = false;
  document.removeEventListener("mousemove", handleDrag);
  document.removeEventListener("mouseup", stopDrag);
  document.removeEventListener("touchmove", handleDrag);
  document.removeEventListener("touchend", stopDrag);
};

// Methods
const setValues = () => {
  registerNoteObj.value.contactId = props.selectedContact?.id ?? null;
  registerNoteObj.value.organizationId = account.value?.organizationId;
};

const closeModal = () => {
  if (noteEditor.value?.hasPendingUploads?.()) {
    showCloseConfirm.value = true;
    return;
  }
  if (!isLoading.value) cleanupDraftIfAny();
  emit("closeNotesModal");
};

const confirmCloseAndDiscard = () => {
  showCloseConfirm.value = false;
  noteEditor.value?.cancelAllUploads?.();
  if (!isLoading.value) cleanupDraftIfAny();
  emit("closeNotesModal");
};

const cleanupDraftIfAny = () => {
  const key = draftKey.value;
  if (!key) return;
  draftKey.value = null;
  discardDraft.mutate(key, { onError: () => {} });
};

const cancelAddNote = () => {
  noteEditor.value?.cancelAllUploads?.();
  showAddNoteForm.value = false;
  registerNoteObj.value.message = null;
  if (noteEditor.value && noteEditor.value.clear) {
    noteEditor.value.clear();
  }
  cleanupDraftIfAny();
  clearNoteTextDraft();
};

const selectNoteToEdit = (note: any) => {
  selectedNote.value = note;
  showEditNoteModal.value = true;
};

const countAttachments = (html: string) => {
  if (!html || typeof html !== "string") return 0;
  const imgs = (html.match(/<img\b/gi) || []).length;
  const vids = (html.match(/<video\b/gi) || []).length;
  const auds = (html.match(/<audio\b/gi) || []).length;
  const iframes = (html.match(/<iframe\b/gi) || []).length;
  const chips = (html.match(/class="[^"]*\brte-file-chip\b[^_]/gi) || []).length;
  return imgs + vids + auds + iframes + chips;
};

const attachmentCounts = computed(() => {
  const map = new Map<string, number>();
  (notes.value || []).forEach((note: any) => {
    map.set(note.id, countAttachments(note.message));
  });
  return map;
});

const attachmentCount = (noteId: string) => attachmentCounts.value.get(noteId) || 0;

const parseNote = (text: string) => {
  if (!text || typeof text !== "string") return "";

  const isHtmlEscaped = (html: string) => {
    if (!html || typeof html !== "string") return false;
    return /&lt;/.test(html) && !/</.test(html);
  };

  const decodeIfEscaped = (html: string) => {
    if (!html || typeof html !== "string") return html || "";
    if (!isHtmlEscaped(html)) {
      return html;
    }
    const div = document.createElement("div");
    div.innerHTML = html;
    return div.innerHTML;
  };

  let decodedText = decodeIfEscaped(text);
  const hasHtmlTags = /<[^>]+>/g.test(decodedText);

  if (hasHtmlTags) {
    return decodedText;
  } else {
    // Plain text: convert URLs to links and newlines to <br>
    return decodedText
      .replace(/\n/g, "<br>")
      .replace(
        /(https?:\/\/[^\s]+)/g,
        '<a href="$1" target="_blank" rel="noopener noreferrer" style="color: #2d60ff; text-decoration: underline;">$1</a>'
      );
  }
};

const getDateOnly = (date: string) => {
  return filters.getDateOnly(date) || new Date(date).toLocaleDateString();
};

const getTimeOnly = (date: string) => {
  return filters.getTimeOnly(date) || new Date(date).toLocaleTimeString();
};

const registerNote = async () => {
  if (pendingUploads.value > 0) {
    toast.warning("Please wait for uploads to finish.");
    return;
  }
  try {
    if (noteEditor.value?.stripAllPlaceholders) {
      noteEditor.value.stripAllPlaceholders();
      const fresh = noteEditor.value?.getHTML?.();
      if (typeof fresh === "string") registerNoteObj.value.message = fresh;
    }
    const { message, contactId, organizationId } = registerNoteObj.value;
    const result = await registerNoteMutation.mutateAsync({
      message: message ?? "",
      contactId: contactId ?? "",
      organizationId: organizationId != null ? organizationId : undefined,
      ...(draftKey.value ? { draftKey: draftKey.value } : {}),
    });

    if (result) {
      if (noteEditor.value && noteEditor.value.clear) {
        noteEditor.value.clear();
      }
      registerNoteObj.value.message = null;
      showAddNoteForm.value = false;
      draftKey.value = null;
      clearNoteTextDraft();
      refetchNotes();
    }
  } catch (error) {
    console.error("Error registering note:", error);
  }
};

const toggleDeleteConfirmationModal = (note: any) => {
  selectedNote.value = note;
  showDeleteConfirmationModal.value = true;
};

const deleteNote = async () => {
  if (!selectedNote.value?.id) return;
  return deleteNoteMutation(selectedNote.value.id);
};

onMounted(() => {
  setValues();
  if (props.startInAddMode) showAddNoteForm.value = true;
});

onBeforeUnmount(() => {
  noteEditor.value?.cancelAllUploads?.();
  if (beforeUnloadHandler) {
    window.removeEventListener("beforeunload", beforeUnloadHandler);
    beforeUnloadHandler = null;
  }
  document.removeEventListener("mousemove", handleDrag);
  document.removeEventListener("mouseup", stopDrag);
  document.removeEventListener("touchmove", handleDrag);
  document.removeEventListener("touchend", stopDrag);
  if (!isLoading.value) cleanupDraftIfAny();
});
</script>

<style scoped>
.note-card-header {
  position: relative;
}
.btn-action.btn-attachments {
  width: auto;
  min-width: 22px;
  padding: 0 6px;
  gap: 3px;
  font-size: 10px;
  font-weight: 600;
  background-color: #eef2ff;
  color: #4338ca;
  border-color: #e0e7ff;
  cursor: default;
}
.btn-action.btn-attachments .iconify {
  width: 12px;
  height: 12px;
}
.btn-action__count {
  line-height: 1;
}

.draggable-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: transparent;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 10070;
  pointer-events: none;
}

.draggable-overlay.dragging-active {
  align-items: flex-start;
  justify-content: flex-start;
}

.draggable-panel {
  background: #ffffff;
  border-radius: 12px;
  width: 480px;
  max-width: 90vw;
  height: 640px;
  max-height: 88vh;
  min-width: 360px;
  min-height: 360px;
  display: flex;
  flex-direction: column;
  border: 1px solid rgba(15, 23, 42, 0.12);
  box-shadow:
    0 1px 2px rgba(15, 23, 42, 0.06),
    0 12px 32px rgba(15, 23, 42, 0.16),
    0 30px 80px rgba(15, 23, 42, 0.22);
  transition: box-shadow 0.2s ease;
  user-select: none;
  pointer-events: auto;
  position: relative;
  resize: both;
  overflow: auto;
}

.draggable-panel.is-dragging {
  box-shadow:
    0 2px 4px rgba(15, 23, 42, 0.08),
    0 20px 48px rgba(15, 23, 42, 0.24),
    0 40px 100px rgba(15, 23, 42, 0.32);
  cursor: grabbing;
  transition: none;
}

.panel-header {
  background: linear-gradient(90deg, #2d3bff 0%, #4f63ff 100%);
  padding: 14px 16px;
  border-radius: 12px 12px 0 0;
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-shrink: 0;
}

.panel-header.draggable {
  cursor: grab;
  user-select: none;
}

.panel-header.draggable:active {
  cursor: grabbing;
}

.panel-header__text {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.panel-header__title {
  margin: 0;
  font-size: 1rem;
  font-weight: 600;
  color: #fff;
  line-height: 1.3;
}

.panel-header__subtitle {
  margin: 0;
  font-size: 0.75rem;
  font-weight: 400;
  color: rgba(255, 255, 255, 0.7);
  line-height: 1.3;
}

.panel-header__actions {
  display: flex;
  align-items: center;
  gap: 8px;
}

.drag-handle {
  color: rgba(255, 255, 255, 0.6);
  cursor: grab;
  padding: 4px;
  border-radius: 4px;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: center;
}

.drag-handle:hover {
  color: rgba(255, 255, 255, 0.9);
  background-color: rgba(255, 255, 255, 0.15);
}

.panel-header__close {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border-radius: 50%;
  border: none;
  background: rgba(255, 255, 255, 0.15);
  color: #fff;
  cursor: pointer;
  transition: background 0.2s;
  flex-shrink: 0;
}

.panel-header__close:hover {
  background: rgba(255, 255, 255, 0.25);
}

.panel-header__close .iconify {
  width: 16px;
  height: 16px;
}

.panel-body {
  flex: 1;
  overflow-y: auto;
  overflow-x: hidden;
  min-height: 0;
}

.panel-footer {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 8px;
  padding: 14px 20px;
  border-top: 1px solid #e5e7eb;
  flex-shrink: 0;
  border-radius: 0 0 12px 12px;
}

.modal-fade-enter-active,
.modal-fade-leave-active {
  transition: opacity 0.3s ease;
}

.modal-fade-enter-from,
.modal-fade-leave-to {
  opacity: 0;
}

.modal-body-content {
  padding: 20px;
  min-height: 280px;
}

.loading-state {
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 260px;
  padding: 24px;
}

.note-card {
  background-color: #ffffff;
  border: 1px solid #e5e7eb;
  border-radius: 12px;
  margin-bottom: 12px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
  transition: all 0.2s ease;
  overflow: hidden;
}

.note-card:hover {
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
  border-color: #d1d5db;
}

.note-card-header {
  padding: 16px;
  display: flex;
  gap: 12px;
  align-items: flex-start;
}

.note-card-footer {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 16px;
  background-color: #f9fafb;
  border-top: 1px solid #f3f4f6;
}

.note-message {
  font-size: 0.875rem;
  line-height: 1.6;
  color: #111827;
  word-wrap: break-word;
  flex: 1;
}

.note-message :deep(h1),
.note-message :deep(h2),
.note-message :deep(h3),
.note-message :deep(h4),
.note-message :deep(h5),
.note-message :deep(h6) {
  margin: 0 0 8px 0;
  font-weight: 600;
  color: #111827;
}

.note-message :deep(p) {
  margin: 0 0 8px 0;
}

.note-message :deep(p:last-child) {
  margin-bottom: 0;
}

.note-message :deep(img) {
  max-width: 100%;
  height: auto;
  border-radius: 8px;
  margin: 12px 0;
  display: block;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.note-message :deep(a) {
  color: #2d60ff;
  text-decoration: underline;
  font-weight: 500;
}

.note-message :deep(a:hover) {
  color: #1e4ed8;
}

.meta-item {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 0.75rem;
  color: #6b7280;
}

.meta-icon {
  width: 14px;
  height: 14px;
  color: #9ca3af;
}

.meta-text {
  font-weight: 500;
}

.meta-date-time {
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 6px;
  line-height: 1.2;
}

.meta-date-text {
  font-weight: 500;
  color: #374151;
  font-size: 0.75rem;
}

.meta-time-text {
  font-size: 0.7rem;
  color: #9ca3af;
  font-weight: 400;
}

.note-actions {
  display: flex;
  gap: 4px;
  flex-shrink: 0;
}

.btn-action {
  width: 22px;
  height: 22px;
  border-radius: 4px;
  border: 1px solid transparent;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.2s ease;
  padding: 0;
}

.btn-action .iconify {
  width: 12px;
  height: 12px;
}

.btn-action.btn-edit {
  background-color: #f3f4f6;
  color: #6b7280;
  border-color: #e5e7eb;
}

.btn-action.btn-edit:hover {
  background-color: #eff6ff;
  color: #2d60ff;
  border-color: #bfdbfe;
}

.btn-action.btn-delete {
  background-color: #fef2f2;
  color: #f87171;
  border-color: #fecaca;
}

.btn-action.btn-delete:hover {
  background-color: #fee2e2;
  color: #ef4444;
  border-color: #fca5a5;
}

.btn-action:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.add-note-form {
  margin-bottom: 16px;
  padding: 16px;
  background-color: #ffffff;
  border-radius: 12px;
  border: 1px solid #e5e7eb;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
}

.add-note-label {
  font-size: 0.875rem;
  font-weight: 600;
  color: #374151;
  margin-bottom: 10px;
  display: block;
}

.add-note-actions {
  display: flex;
  gap: 10px;
  justify-content: flex-end;
  align-items: center;
  margin-top: 14px;
  padding-top: 14px;
  border-top: 1px solid #f3f4f6;
}

.empty-notes {
  padding: 40px 24px;
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 280px;
}

.empty-notes__inner {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;
}

.empty-icon-wrapper {
  width: 56px;
  height: 56px;
  border-radius: 16px;
  background: linear-gradient(135deg, #eef2ff 0%, #e0e7ff 100%);
  border: 1px solid #c7d2fe;
  display: flex;
  align-items: center;
  justify-content: center;
}

.empty-icon {
  color: #6366f1;
}

.empty-text {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 6px;
  text-align: center;
  max-width: 280px;
}

.empty-title {
  font-size: 0.9375rem;
  font-weight: 600;
  color: #111827;
  margin: 0;
}

.empty-description {
  color: #6b7280;
  font-size: 0.8125rem;
  line-height: 1.5;
  margin: 0;
}

:deep(.ql-toolbar) {
  padding: 4px 8px;
  font-size: 10px;
  border-top-left-radius: 8px;
  border-top-right-radius: 8px;
}

:deep(.ql-toolbar .ql-formats) {
  margin-right: 6px;
}

:deep(.ql-toolbar button) {
  padding: 2px 4px;
  font-size: 10px;
  width: 20px;
  height: 20px;
}

:deep(.ql-toolbar .ql-picker) {
  font-size: 10px;
}

:deep(.ql-container) {
  border-bottom-left-radius: 8px;
  border-bottom-right-radius: 8px;
  font-size: 0.9rem;
}
</style>
